import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../../design-system';
import { BUTTON_PRESETS } from '../../design-system/constants';
import { Form, Input, Select, InputNumber, DatePicker, message } from 'antd';
import { roleService } from '../../services/roleService';
import moment from 'moment';
import { getApiErrorMessage } from '../../utils/errorUtils';
import logger from '../../utils/logger';

const isActiveMember = (member) => member?.is_active !== false;

const memberSearchLabel = (member) => [
    member.full_name,
    member.job_title,
    member.user_role?.name,
    isActiveMember(member) ? 'active' : 'inactive'
].filter(Boolean).join(' ');

const renderStatusBadge = (isActive) => (
    <span
        style={{
            marginLeft: 8,
            padding: '1px 6px',
            borderRadius: 4,
            fontSize: 11,
            border: `1px solid ${isActive ? '#b7eb8f' : '#d9d9d9'}`,
            color: isActive ? '#237804' : '#8c8c8c',
            background: isActive ? '#f6ffed' : '#f5f5f5'
        }}
    >
        {isActive ? 'Active' : 'Inactive'}
    </span>
);

const renderMemberOption = (member, currentRoleId) => (
    <span>
        <span>{member.full_name}</span>
        {member.job_title ? <span style={{ color: '#8c8c8c' }}> - {member.job_title}</span> : null}
        {member.user_role && Number(member.user_role.id) !== Number(currentRoleId)
            ? <span style={{ color: '#8c8c8c' }}> ({member.user_role.name})</span>
            : null}
        {renderStatusBadge(isActiveMember(member))}
    </span>
);

const RoleFormModal = ({ visible, role, onClose }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [teamMembersLoading, setTeamMembersLoading] = useState(false);
    const selectedDepartmentId = Form.useWatch('department_id', form);

    const loadDepartments = useCallback(async () => {
        try {
            const result = await roleService.getManagedDepartments();
            const deptList = result.departments || [];
            setDepartments(deptList);
        } catch (error) {
            logger.error('Error loading departments:', error);
            message.error('Failed to load departments');
        }
    }, []);

    const loadTeamMembers = useCallback(async (departmentId) => {
        if (!departmentId) {
            setTeamMembers([]);
            form.setFieldsValue({ team_member_ids: [] });
            return;
        }

        try {
            setTeamMembersLoading(true);
            const result = await roleService.getDepartmentTeamMembers(departmentId, { includeInactive: true });
            const members = result.team_members || [];
            setTeamMembers(members);

            const selectedIds = members
                .filter(member => Number(member.user_role_id) === Number(role?.id))
                .map(member => member.id);

            form.setFieldsValue({ team_member_ids: role ? selectedIds : [] });
        } catch (error) {
            logger.error('Error loading team members:', error);
            message.error('Failed to load team members');
        } finally {
            setTeamMembersLoading(false);
        }
    }, [form, role]);

    useEffect(() => {
        if (visible) {
            loadDepartments();
            setTeamMembers([]);
            if (role) {
                form.setFieldsValue({
                    name: role.name,
                    responsibilities: role.responsibilities || '',
                    department_id: role.department_id,
                    rate: Number(role.current_rate || 0),
                    valid_at: moment()
                });
            } else {
                form.resetFields();
                form.setFieldsValue({
                    responsibilities: '',
                    valid_at: moment()
                });
            }
        }
    }, [visible, role, form, loadDepartments]);

    useEffect(() => {
        if (visible && selectedDepartmentId) {
            loadTeamMembers(selectedDepartmentId);
        }
    }, [visible, selectedDepartmentId, loadTeamMembers]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            const teamMemberIds = values.team_member_ids || [];

            // Format valid_at for API
            if (values.valid_at) {
                values.valid_at = values.valid_at.format('YYYY-MM-DD');
            }

            delete values.team_member_ids;

            if (role) {
                await roleService.updateRole(role.id, values);
                await roleService.updateRoleTeamMembers(role.id, teamMemberIds);
                message.success('Role updated successfully');
            } else {
                const createdRole = await roleService.createRole(values);
                if (teamMemberIds.length > 0) {
                    await roleService.updateRoleTeamMembers(createdRole.id, teamMemberIds);
                }
                message.success('Role created successfully');
            }

            onClose(true); // Refresh list
        } catch (error) {
            if (getApiErrorMessage(error, "").includes('already exists')) {
                message.error(`Role "${form.getFieldValue('name')}" already exists in this department`);
            } else if (error.errorFields) {
                // Ant Design validation errors
                message.error('Please fill in all required fields');
            } else {
                logger.error('Save error:', error);
                message.error('Failed to save role');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal visible={visible} onClose={() => onClose(false)} width="medium">
            <Modal.Header title={role ? 'Edit role' : 'Add role'} />
            <Modal.Body padding="default">
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Role Name"
                        name="name"
                        rules={[
                            { required: true, message: 'Please enter name' },
                            { max: 255, message: 'Maximum 255 characters' }
                        ]}
                    >
                        <Input placeholder="e.g., Senior Developer, QA Lead" maxLength={255} />
                    </Form.Item>

                    <Form.Item
                        label="Department"
                        name="department_id"
                        rules={[
                            { required: true, message: 'Please select a department' }
                        ]}
                    >
                        <Select
                            placeholder="Select department"
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {departments.map(dept => (
                                <Select.Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Responsibilities"
                        name="responsibilities"
                    >
                        <Input.TextArea
                            autoSize={{ minRows: 4, maxRows: 8 }}
                            placeholder={"- Lead delivery\n- Review work\n- Support client questions"}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Public Rate (SGD/hour)"
                        name="rate"
                        rules={[
                            { required: true, message: 'Please enter rate' },
                            { type: 'number', min: 0, message: 'Rate must be 0 or greater' }
                        ]}
                    >
                        <InputNumber
                            placeholder="e.g., 125"
                            min={0}
                            precision={2}
                            style={{ width: '100%' }}
                            addonBefore="SGD"
                            addonAfter="/hour"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Effective Date"
                        name="valid_at"
                        rules={[
                            { required: true, message: 'Please select effective date' }
                        ]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Team Members"
                        name="team_member_ids"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select team members"
                            loading={teamMembersLoading}
                            disabled={!selectedDepartmentId}
                            showSearch
                            optionFilterProp="label"
                        >
                            {teamMembers.map(member => (
                                <Select.Option key={member.id} value={member.id} label={memberSearchLabel(member)}>
                                    {renderMemberOption(member, role?.id)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal.Body>
            <Modal.Footer buttons={[
                {
                    ...BUTTON_PRESETS.SAVE,
                    label: role ? 'Update' : 'Create',
                    loading,
                    onClick: handleSave
                }
            ]} />
        </Modal>
    );
};

export default RoleFormModal;
